<template>
  <div class="time-range-override-dropdown">
    <button
      ref="buttonRef"
      class="dropdown-trigger"
      :class="{ 'is-enabled': hasOverride, 'is-open': isOpen }"
      @click="toggleDropdown"
    >
      <font-awesome-icon :icon="['fas', 'clock']" class="dropdown-trigger-icon" />
      <span class="dropdown-trigger-value">{{ displayLabel }}</span>
      <font-awesome-icon :icon="['fas', 'chevron-down']" class="dropdown-trigger-chevron" />
    </button>

    <Teleport to="body">
      <div
        v-if="isOpen"
        class="override-dropdown-menu"
        :style="dropdownStyles"
      >
        <div
          v-for="option in timeRangeOptions"
          :key="option.value"
          class="frequency-option"
          :class="{ 'is-selected': globalTimeRange === option.value }"
          @click="selectTimeRange(option.value)"
        >
          <span class="option-check-space">
            <font-awesome-icon v-if="globalTimeRange === option.value" :icon="['fas', 'check']" class="option-check" />
          </span>
          <span class="option-label">{{ option.label }}</span>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useGlobalDashboardSettings } from '@/composables/useGlobalDashboardSettings'
import { GLOBAL_TIME_RANGE_OPTIONS } from '@/config/dashboardOptions'

// Time range options from shared constants
const timeRangeOptions = GLOBAL_TIME_RANGE_OPTIONS

const isOpen = ref(false)
const buttonRef = ref(null)
const dropdownStyles = ref({})

const { globalTimeRange, hasTimeRangeOverride, setGlobalTimeRange } = useGlobalDashboardSettings()

const displayLabel = computed(() => {
  const option = timeRangeOptions.find(o => o.value === globalTimeRange.value)
  return option ? option.label : 'Per Widget'
})

const hasOverride = computed(() => hasTimeRangeOverride.value)

function toggleDropdown() {
  isOpen.value = !isOpen.value
  if (isOpen.value) {
    calculateDropdownPosition()
  }
}

function selectTimeRange(value) {
  setGlobalTimeRange(value)
  isOpen.value = false
}

function calculateDropdownPosition() {
  if (!buttonRef.value) return

  const rect = buttonRef.value.getBoundingClientRect()
  const dropdownHeight = 400 // Approximate
  const spaceBelow = window.innerHeight - rect.bottom
  const spaceAbove = rect.top

  if (spaceBelow >= dropdownHeight || spaceBelow > spaceAbove) {
    // Position below
    dropdownStyles.value = {
      position: 'fixed',
      top: `${rect.bottom + 8}px`,
      left: `${rect.left}px`,
      minWidth: `${rect.width}px`
    }
  } else {
    // Position above
    dropdownStyles.value = {
      position: 'fixed',
      bottom: `${window.innerHeight - rect.top + 8}px`,
      left: `${rect.left}px`,
      minWidth: `${rect.width}px`
    }
  }
}

function handleClickOutside(event) {
  if (isOpen.value && buttonRef.value && !buttonRef.value.contains(event.target)) {
    const dropdown = document.querySelector('.override-dropdown-menu')
    if (dropdown && !dropdown.contains(event.target)) {
      isOpen.value = false
    }
  }
}

function handleEscape(event) {
  if (event.key === 'Escape' && isOpen.value) {
    isOpen.value = false
  }
}

function handleResize() {
  if (isOpen.value) {
    calculateDropdownPosition()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleEscape)
  window.addEventListener('resize', handleResize)
  window.addEventListener('scroll', handleResize)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleEscape)
  window.removeEventListener('resize', handleResize)
  window.removeEventListener('scroll', handleResize)
})
</script>

<style scoped>
.time-range-override-dropdown {
  position: relative;
  display: inline-flex;
}

/* Trigger styles (.dropdown-trigger and children) come from
   @/styles/dropdown-trigger.css, imported globally in main.js. */

/* Dropdown menu */
.override-dropdown-menu {
  background: var(--surface-page);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  z-index: 9999;
  overflow: hidden;
  padding: 0.5rem 0;
}

.frequency-option {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.75rem 1rem;
  cursor: pointer;
  transition: background 0.15s ease;
}

.frequency-option:hover {
  background: var(--surface-overlay);
}

.frequency-option.is-selected {
  background: rgba(139, 92, 246, 0.08);
}

.frequency-option.is-selected:hover {
  background: rgba(139, 92, 246, 0.12);
}

.option-check-space {
  width: 1rem;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
}

.option-check {
  font-size: 0.75rem;
  color: var(--accent-text);
}

.option-label {
  font-size: 0.875rem;
  color: var(--text-primary);
}
</style>
